// Public Share View Page - Standalone content viewer without platform access
import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  BookOpen, FileText, ClipboardList, HelpCircle, Lock, Eye, 
  Download, Calendar, ChevronLeft, ChevronRight, Loader2,
  AlertCircle, Globe
} from 'lucide-react';
import { toast } from 'sonner';
import { HtmlContentWithPdfSlides } from '@/components/editor/PdfSlidesRenderer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Script to inject in iframe srcdoc to handle anchor links properly
const iframeNavScript = `<script data-nav-handler="true">
  document.addEventListener('click', function(e) {
    var link = e.target.closest('a');
    if (!link) return;
    var href = link.getAttribute('href');
    if (!href) return;
    
    if (href.startsWith('#')) {
      e.preventDefault();
      e.stopPropagation();
      var targetId = href.slice(1);
      var target = document.getElementById(targetId) || document.querySelector('[name="' + targetId + '"]');
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return false;
    }
    
    if (href.startsWith('http://') || href.startsWith('https://')) {
      e.preventDefault();
      window.open(href, '_blank', 'noopener,noreferrer');
      return false;
    }
  }, true);
<\/script>`;

// Helper function to process HTML content and ensure iframe links stay within iframe
const processHtmlContent = (content) => {
  if (!content) return '';
  
  // Strip scripts and orphaned JS code using DOMParser
  let cleanedContent;
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');
    doc.querySelectorAll('script, noscript').forEach(el => el.remove());
    const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
    const toRemove = [];
    while (walker.nextNode()) {
      const t = walker.currentNode.textContent.trim();
      if (t && (t.includes('navigator.clipboard') || t.includes('.cmd-box') || t.includes('.querySelectorAll') || t.includes('.querySelector(') || (t.includes('button.textContent') && t.includes('setTimeout')))) {
        toRemove.push(walker.currentNode);
      }
    }
    toRemove.forEach(n => n.parentNode.removeChild(n));
    cleanedContent = doc.body.innerHTML;
  } catch (e) {
    cleanedContent = content.replace(/<script\b[^>]*>[\s\S]*?<\/\s*script\s*>/gi, '');
  }
  
  // Clean up problematic classes that block interactions
  cleanedContent = cleanedContent
    .replace(/pointer-events-none\s*/g, '')
    .replace(/select-none\s*/g, '');
  
  // Find all iframes with srcdoc and add navigation script to their content
  return cleanedContent.replace(/(<iframe[^>]*srcdoc=["'])([^"']*)(["'][^>]*>)/gi, (match, prefix, srcdoc, suffix) => {
    // Decode HTML entities in srcdoc
    let decodedSrcdoc = srcdoc
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");
    
    // Add navigation handler script if not present
    if (!decodedSrcdoc.includes('data-nav-handler')) {
      if (decodedSrcdoc.includes('</body>')) {
        decodedSrcdoc = decodedSrcdoc.replace('</body>', iframeNavScript + '</body>');
      } else if (decodedSrcdoc.includes('</html>')) {
        decodedSrcdoc = decodedSrcdoc.replace('</html>', iframeNavScript + '</html>');
      } else {
        decodedSrcdoc = decodedSrcdoc + iframeNavScript;
      }
      
      // Also add base target
      if (!decodedSrcdoc.includes('<base')) {
        if (decodedSrcdoc.includes('<head>')) {
          decodedSrcdoc = decodedSrcdoc.replace('<head>', '<head><base target="_self">');
        } else if (decodedSrcdoc.includes('<head ')) {
          decodedSrcdoc = decodedSrcdoc.replace(/<head([^>]*)>/, '<head$1><base target="_self">');
        } else if (decodedSrcdoc.includes('<html')) {
          decodedSrcdoc = decodedSrcdoc.replace(/<html([^>]*)>/i, '<html$1><head><base target="_self"></head>');
        }
      }
    }
    
    // Re-encode for srcdoc attribute
    const encodedSrcdoc = decodedSrcdoc
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
    
    return prefix + encodedSrcdoc + suffix;
  });
};

// Helper to add navigation handler to raw HTML for quiz custom_html
const addBaseTargetToHtml = (html) => {
  if (!html) return html;
  
  let processed = html;
  
  // Add navigation handler script if not present
  if (!processed.includes('data-nav-handler')) {
    if (processed.includes('</body>')) {
      processed = processed.replace('</body>', iframeNavScript + '</body>');
    } else if (processed.includes('</html>')) {
      processed = processed.replace('</html>', iframeNavScript + '</html>');
    } else {
      processed = processed + iframeNavScript;
    }
  }
  
  // Add base target if not present
  if (!processed.includes('<base')) {
    if (processed.includes('<head>')) {
      processed = processed.replace('<head>', '<head><base target="_self">');
    } else if (processed.includes('<head ')) {
      processed = processed.replace(/<head([^>]*)>/, '<head$1><base target="_self">');
    } else if (processed.includes('<html')) {
      processed = processed.replace(/<html([^>]*)>/i, '<html$1><head><base target="_self"></head>');
    } else if (processed.includes('<!DOCTYPE')) {
      processed = processed.replace(/(<!DOCTYPE[^>]*>)/i, '$1<head><base target="_self"></head>');
    }
  }
  
  return processed;
};

export default function ShareViewPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState('');
  const [needsPassword, setNeedsPassword] = useState(false);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  
  // Check if fullscreen mode is requested
  const isFullscreen = searchParams.get('fullscreen') === 'true';

  useEffect(() => {
    loadContent();
  }, [token]);

  const loadContent = async (pwd = null) => {
    setLoading(true);
    setError(null);
    
    try {
      const url = pwd 
        ? `${API_URL}/api/share/${token}?password=${encodeURIComponent(pwd)}`
        : `${API_URL}/api/share/${token}`;
      
      const response = await fetch(url);
      const result = await response.json();
      
      if (response.status === 404) {
        setError('Ce lien de partage est invalide ou a été supprimé.');
        return;
      }
      
      if (response.status === 410) {
        setError('Ce lien de partage a expiré.');
        return;
      }
      
      if (result.requires_password) {
        setNeedsPassword(true);
        setData({ title: result.title, content_type: result.content_type });
        return;
      }
      
      setNeedsPassword(false);
      setData(result);
    } catch (err) {
      console.error('Failed to load shared content:', err);
      setError('Erreur lors du chargement du contenu.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (password.trim()) {
      loadContent(password);
    }
  };

  const getContentIcon = (type) => {
    switch (type) {
      case 'course': return <BookOpen className="h-6 w-6" />;
      case 'section': return <FileText className="h-6 w-6" />;
      case 'tp': return <ClipboardList className="h-6 w-6" />;
      case 'quiz': return <HelpCircle className="h-6 w-6" />;
      case 'assignment': return <FileText className="h-6 w-6" />;
      case 'site': return <Globe className="h-6 w-6" />;
      default: return <FileText className="h-6 w-6" />;
    }
  };

  const getContentTypeLabel = (type) => {
    switch (type) {
      case 'course': return 'Cours';
      case 'section': return 'Section';
      case 'tp': return 'TP';
      case 'quiz': return 'Quiz';
      case 'assignment': return 'Devoir';
      case 'site': return 'Site Statique';
      default: return 'Contenu';
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-purple-500 mx-auto mb-4" />
          <p className="text-slate-400">Chargement...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-slate-800/50 border-slate-700">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Lien invalide</h2>
            <p className="text-slate-400">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Password required
  if (needsPassword) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-slate-800/50 border-slate-700">
          <CardHeader className="text-center">
            <div className="h-16 w-16 rounded-full bg-purple-600/20 flex items-center justify-center mx-auto mb-4">
              <Lock className="h-8 w-8 text-purple-400" />
            </div>
            <CardTitle className="text-white">{data?.title || 'Contenu protégé'}</CardTitle>
            <p className="text-slate-400">Ce contenu est protégé par un mot de passe</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <Input
                type="password"
                placeholder="Mot de passe"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-slate-900 border-slate-700 text-white"
              />
              <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700">
                Accéder au contenu
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Render content based on type
  const renderContent = () => {
    const content = data?.content;
    if (!content) return null;

    switch (data.content_type) {
      case 'course':
        return <CourseView content={content} currentSectionIndex={currentSectionIndex} setCurrentSectionIndex={setCurrentSectionIndex} />;
      case 'section':
        return <SectionView content={content} />;
      case 'tp':
        return <TPView content={content} />;
      case 'quiz':
        return <QuizView content={content} />;
      case 'assignment':
        return <AssignmentView content={content} />;
      case 'site':
        return <SiteView content={content} backendUrl={data.backend_url} shareToken={token} />;
      default:
        return <GenericView content={content} />;
    }
  };

  // Fullscreen mode for sites - just show the iframe
  if (isFullscreen && data?.content_type === 'site' && data?.content) {
    const baseUrl = data.backend_url || API_URL;
    const siteUrl = `${baseUrl}/s/${data.content.id}/`;
    
    return (
      <div className="h-screen w-screen">
        <iframe
          src={siteUrl}
          className="w-full h-full border-0"
          title={data.content.name}
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900">
      <style>{`
        [data-google-slides] .aspect-video::after {
          content: '';
          position: absolute;
          bottom: 0;
          right: 0;
          width: 140px;
          height: 38px;
          background-color: #f1f3f4;
          pointer-events: all;
          z-index: 10;
        }
        .bg-slate-950 [data-google-slides] .aspect-video::after,
        .dark [data-google-slides] .aspect-video::after {
          background-color: #2b2b2b;
        }
      `}</style>
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/80 backdrop-blur sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-purple-600/20 flex items-center justify-center">
              {getContentIcon(data?.content_type)}
            </div>
            <div>
              <h1 className="text-lg font-semibold text-white">{data?.title}</h1>
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <Badge variant="outline" className="text-xs">{getContentTypeLabel(data?.content_type)}</Badge>
                <span className="flex items-center gap-1">
                  <Eye className="h-3 w-3" />
                  {data?.views_count} vue(s)
                </span>
              </div>
            </div>
          </div>
          {data?.allow_download && (
            <Button variant="outline" size="sm" className="border-slate-700 text-slate-300">
              <Download className="h-4 w-4 mr-2" />
              Télécharger
            </Button>
          )}
        </div>
      </header>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 py-8">
        {renderContent()}
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-6 mt-8">
        <div className="max-w-5xl mx-auto px-4 flex items-center justify-center gap-2 text-sm text-slate-500">
          <span>Partagé via DevOpsLearn</span>
          <img src="/logo.png" alt="EFI Academy" className="h-5 w-auto object-contain opacity-75 hover:opacity-100 transition-opacity" />
        </div>
      </footer>
    </div>
  );
}

// ========================== SUB-COMPONENTS ==========================

function CourseView({ content, currentSectionIndex, setCurrentSectionIndex }) {
  const sections = content.sections || [];
  const currentSection = sections[currentSectionIndex];

  return (
    <div className="space-y-6">
      {/* Course Header */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">{content.title}</h2>
        {content.description && (
          <p className="text-slate-400">{content.description}</p>
        )}
        <div className="flex items-center gap-4 mt-4 text-sm text-slate-500">
          <span>{sections.length} section(s)</span>
        </div>
      </div>

      {/* No sections */}
      {sections.length === 0 && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="py-12 text-center">
            <BookOpen className="h-12 w-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400 text-lg">Ce cours n'a pas encore de sections</p>
            <p className="text-slate-500 text-sm mt-2">Le contenu sera disponible prochainement</p>
          </CardContent>
        </Card>
      )}

      {/* Sections Navigation */}
      {sections.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6">
          {sections.map((section, idx) => (
            <Button
              key={section.id || idx}
              variant={idx === currentSectionIndex ? 'default' : 'outline'}
              size="sm"
              onClick={() => setCurrentSectionIndex(idx)}
              className={idx === currentSectionIndex ? 'bg-purple-600' : 'border-slate-700 text-slate-300'}
            >
              {idx + 1}. {section.title?.slice(0, 20) || `Section ${idx + 1}`}
            </Button>
          ))}
        </div>
      )}

      {/* Current Section Content */}
      {currentSection && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">{currentSection.title}</CardTitle>
            {currentSection.description && (
              <p className="text-slate-400 text-sm">{currentSection.description}</p>
            )}
          </CardHeader>
          <CardContent>
            <HtmlContentWithPdfSlides 
              html={processHtmlContent(currentSection.content) || '<p class="text-slate-500">Pas de contenu</p>'}
              className="prose prose-invert max-w-none"
            />
          </CardContent>
        </Card>
      )}

      {/* Navigation */}
      {sections.length > 1 && (
        <div className="flex items-center justify-between pt-4">
          <Button
            variant="outline"
            onClick={() => setCurrentSectionIndex(Math.max(0, currentSectionIndex - 1))}
            disabled={currentSectionIndex === 0}
            className="border-slate-700 text-slate-300"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Précédent
          </Button>
          <span className="text-slate-500">
            {currentSectionIndex + 1} / {sections.length}
          </span>
          <Button
            variant="outline"
            onClick={() => setCurrentSectionIndex(Math.min(sections.length - 1, currentSectionIndex + 1))}
            disabled={currentSectionIndex === sections.length - 1}
            className="border-slate-700 text-slate-300"
          >
            Suivant
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
}

function SectionView({ content }) {
  return (
    <Card className="bg-slate-800/50 border-slate-700">
      <CardHeader>
        <div className="flex items-center gap-2 text-sm text-slate-400 mb-2">
          <BookOpen className="h-4 w-4" />
          {content.course_title && <span>Cours: {content.course_title}</span>}
        </div>
        <CardTitle className="text-white">{content.title}</CardTitle>
        {content.description && (
          <p className="text-slate-400">{content.description}</p>
        )}
      </CardHeader>
      <CardContent>
        <HtmlContentWithPdfSlides 
          html={processHtmlContent(content.content) || '<p class="text-slate-500">Pas de contenu</p>'}
          className="prose prose-invert max-w-none"
        />
      </CardContent>
    </Card>
  );
}

function TPView({ content }) {
  return (
    <div className="space-y-6">
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white">{content.title}</CardTitle>
          {content.description && (
            <p className="text-slate-400">{content.description}</p>
          )}
          {content.due_date && (
            <div className="flex items-center gap-2 text-sm text-slate-400 mt-2">
              <Calendar className="h-4 w-4" />
              Date limite: {new Date(content.due_date).toLocaleDateString('fr-FR')}
            </div>
          )}
        </CardHeader>
        <CardContent>
          <div 
            className="prose prose-invert max-w-none"
            dangerouslySetInnerHTML={{ __html: processHtmlContent(content.content || content.instructions) || '<p class="text-slate-500">Pas d\'instructions</p>' }}
          />
        </CardContent>
      </Card>
    </div>
  );
}

function QuizView({ content }) {
  const [answers, setAnswers] = useState({});
  const questions = content.questions || [];
  const hasCustomHtml = !!content.custom_html;

  return (
    <div className="space-y-6">
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white">{content.title}</CardTitle>
          {content.description && (
            <p className="text-slate-400">{content.description}</p>
          )}
          <div className="text-sm text-slate-400">
            {hasCustomHtml ? 'Quiz personnalisé' : `${questions.length} question(s)`}
          </div>
        </CardHeader>
      </Card>

      {/* Custom HTML Quiz */}
      {hasCustomHtml && (
        <Card className="bg-slate-800/50 border-slate-700 overflow-hidden">
          <CardContent className="p-0">
            <iframe
              srcDoc={addBaseTargetToHtml(content.custom_html)}
              className="w-full border-0"
              style={{ height: `${content.custom_html_height || 600}px` }}
              title={content.title}
              sandbox="allow-scripts allow-same-origin allow-forms"
            />
          </CardContent>
        </Card>
      )}

      {/* Standard Questions (if any, even with custom HTML) */}
      {questions.length > 0 && (
        <>
          {hasCustomHtml && (
            <div className="text-center py-4">
              <p className="text-slate-400 text-sm">Questions supplémentaires</p>
            </div>
          )}
          {questions.map((question, idx) => (
            <Card key={question.id || idx} className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-6">
                <p className="text-white font-medium mb-4">
                  {idx + 1}. {question.question_text || question.question || question.text}
                </p>
                <div className="space-y-2">
                  {(question.options || question.choices || []).map((option, optIdx) => (
                    <label
                      key={optIdx}
                      className="flex items-center gap-3 p-3 rounded-lg border border-slate-700 hover:border-purple-500 cursor-pointer transition-colors"
                    >
                      <input
                        type="radio"
                        name={`question-${idx}`}
                        value={optIdx}
                        onChange={() => setAnswers({ ...answers, [idx]: optIdx })}
                        className="text-purple-600"
                      />
                      <span className="text-slate-300">{typeof option === 'string' ? option : option.text}</span>
                    </label>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </>
      )}

      {/* Empty state if no content */}
      {!hasCustomHtml && questions.length === 0 && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-8 text-center">
            <HelpCircle className="h-12 w-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">Ce quiz n'a pas encore de contenu</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function AssignmentView({ content }) {
  return (
    <Card className="bg-slate-800/50 border-slate-700">
      <CardHeader>
        <CardTitle className="text-white">{content.title}</CardTitle>
        {content.description && (
          <p className="text-slate-400">{content.description}</p>
        )}
        {content.due_date && (
          <div className="flex items-center gap-2 text-sm text-orange-400 mt-2">
            <Calendar className="h-4 w-4" />
            À rendre avant: {new Date(content.due_date).toLocaleDateString('fr-FR', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </div>
        )}
      </CardHeader>
      <CardContent>
        <div 
          className="prose prose-invert max-w-none"
          dangerouslySetInnerHTML={{ __html: content.instructions || content.content || '<p class="text-slate-500">Pas d\'instructions</p>' }}
        />
      </CardContent>
    </Card>
  );
}

function GenericView({ content }) {
  return (
    <Card className="bg-slate-800/50 border-slate-700">
      <CardContent className="p-6">
        <pre className="text-slate-300 whitespace-pre-wrap">
          {JSON.stringify(content, null, 2)}
        </pre>
      </CardContent>
    </Card>
  );
}

function SiteView({ content, backendUrl, shareToken }) {
  // Use provided backend URL or fallback to API_URL
  const baseUrl = backendUrl || API_URL;
  const siteUrl = `${baseUrl}/s/${content.id}/`;
  
  // For fullscreen, use the share URL with fullscreen param if we have a token
  const fullscreenUrl = shareToken 
    ? `${window.location.origin}/share/${shareToken}?fullscreen=true`
    : siteUrl;
  
  return (
    <div className="space-y-4">
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-white flex items-center gap-2">
                <Globe className="h-5 w-5 text-purple-400" />
                {content.name}
              </CardTitle>
              <p className="text-slate-400 text-sm mt-1">
                {content.pages?.length || 0} pages • {content.source === 'git' ? `Git (${content.provider})` : 'ZIP'}
              </p>
            </div>
            <Button
              variant="outline"
              className="border-purple-500/50 text-purple-300 hover:bg-purple-500/20"
              onClick={() => window.open(fullscreenUrl, '_blank')}
            >
              Ouvrir en plein écran
            </Button>
          </div>
        </CardHeader>
      </Card>
      
      {/* Site iframe */}
      <Card className="bg-slate-800/50 border-slate-700 overflow-hidden">
        <CardContent className="p-0">
          <iframe
            src={siteUrl}
            className="w-full border-0"
            style={{ height: '80vh', minHeight: '600px' }}
            title={content.name}
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
          />
        </CardContent>
      </Card>
    </div>
  );
}
